import { todayPT } from "@homebound/activesupport";
import {
  applyRowFn,
  assignDefaultColumnIds,
  GridColumn,
  GridDataRow,
  Kinded,
  matchesFilter,
  RadioFieldOption,
  reservedRowKinds,
} from "@homebound/beam";
import { FormObjectState, ObjectConfig, required } from "@homebound/form-state";
import {
  BidContractType,
  CostType,
  InternalEstimateType,
  Maybe,
  SaveBidContractInput,
  SaveBidPlanGroupInput,
} from "src/generated/graphqlTypes";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { PlanBasedBidPackagePage_BidPackageFragment } from "src/routes/bids/plan-based-bid-package/PlanBasedBidPackagePage.generated";
import { fail } from "src/utils";
import { Temporal } from "temporal-polyfill";
import {
  PlanBasedBidsTableV2_BidPackageFragment,
  PlanBasedBidsTableV2_BidPlanGroupDimensionFragment,
} from "./components/PlanBasedBidsTableV2.generated";

export const INTERNAL_ESTIMATE_ID = "hb";

export const internalEstimateTypeOptions: RadioFieldOption<InternalEstimateType>[] = [
  {
    label: "Provisional Estimate",
    value: InternalEstimateType.Provisional,
    description: "A temporary cost until we award a trade.",
  },
  {
    label: "Project Estimate",
    value: InternalEstimateType.Project,
    description:
      "An estimated cost that won't be known until the project starts (e.g. Site Clearing), or a cost that has no trade (e.g. Permits).",
  },
];

/**
 * The `saveBidContract` input for putting the internal estimate on a bid package: unarchives the package's
 * estimate when it was removed from the Bidders tab, otherwise creates one.
 */
export function internalEstimateInput(
  bidPackage: {
    id: string;
    isPlanBasedV2: boolean;
    isUnitBased: boolean;
    internalEstimateBidContract?: Maybe<{ id: string; archivedAt?: Maybe<Temporal.ZonedDateTime> }>;
  },
  internalEstimateType: SaveBidContractInput["internalEstimateType"],
): SaveBidContractInput {
  const estimate = bidPackage.internalEstimateBidContract;
  return estimate?.archivedAt
    ? { id: estimate.id, archive: false, internalEstimateType }
    : {
        isInternalEstimate: true,
        internalEstimateBidPackageId: bidPackage.id,
        internalEstimateType,
        startDate: todayPT(),
        type: bidPackage.isPlanBasedV2
          ? BidContractType.UserCreatedGroupBased
          : bidPackage.isUnitBased
            ? BidContractType.UnitBased
            : BidContractType.GroupedByPlanAndOption,
      };
}

export enum PlanBasedBidPackageStep {
  DEFINE_PACKAGE = "Define Package",
  CONFIGURE_BID_GROUPS = "Configure Bid Groups",
  REVIEW_AND_DOWNLOAD = "Review and Download",
}

export enum BaseBidType {
  Standard = "STANDARD",
  Personalization = "PERSONALIZATION",
  None = "NONE",
}

/** How trades cost a multi-cost-code package: one cost per cost code, or one cost on a primary that covers the rest. */
export enum PricingMode {
  Separate = "SEPARATE",
  Primary = "PRIMARY",
}

export type PlanBasedBidPackageRequest = {
  id?: Maybe<string>;
  tradePartnerId?: Maybe<string>;
  archive?: Maybe<boolean>;
};

export type PlanBasedBidPackageBidPlanGroupDimension = {
  id?: Maybe<string>;
  // A dimension is either type-based (globalOptionTypeId) or group-based (globalOptionGroupId), never both.
  globalOptionTypeId?: Maybe<string>;
  globalOptionGroupId?: Maybe<string>;
  globalOptionIds?: Maybe<string[]>;
};

export type PlanBasedBidPackageBidPlanGroup = {
  id?: Maybe<string>;
  isNoCost?: Maybe<boolean>;
  globalOptionId?: Maybe<string>;
  // The "primary" personalization GlobalOptionGroup.
  primaryOptionGroupId?: Maybe<string>;
  order?: Maybe<number>;
  dimensions?: Maybe<PlanBasedBidPackageBidPlanGroupDimension[]>;
  // FE-only soft delete flag, lets toggle off and on groups without losing its persisted ID.
  archive?: Maybe<boolean>;
  // FE-only stable identity for unsaved groups, which have no id yet (and add-on groups can share a
  // globalOptionId). Used for React keys and drag-and-drop; never sent to the BE. Named `clientId` (not `key`)
  // because form-state reserves `key` for its own list-row reconciliation.
  clientId?: Maybe<string>;
};

export type PlanBasedBidPackageFormState = {
  bidPackageGroupId?: Maybe<string>;
  bidPackageId?: Maybe<string>;
  marketId?: Maybe<string>;
  projects?: Maybe<string[]>;
  costCodes?: Maybe<string[]>;
  costType?: Maybe<CostType>;
  internalEstimateType?: Maybe<InternalEstimateType>;
  productOfferings?: Maybe<string[]>;
  requests?: Maybe<PlanBasedBidPackageRequest[]>;
  bidPlanGroups?: Maybe<PlanBasedBidPackageBidPlanGroup[]>;
  baseBidType?: Maybe<BaseBidType>;
  pricingMode?: Maybe<PricingMode>;
  primaryCostCodeId?: Maybe<string>;
};

export type PlanBasedBidPackageFormObjectState = FormObjectState<
  PlanBasedBidPackageFormState,
  PlanBasedBidPackagePage_BidPackageFragment | undefined
>;

export type ProductOfferingObject = {
  __typename?: "ProductOffering" | undefined;
  displayName: string;
  id: string;
  name: string;
};

export type PlanBasedBidPackageWizard = {
  formState: PlanBasedBidPackageFormObjectState;
  /** Persists the form, resolving with `undefined` when the user declines the cost removal confirmation. */
  save: () => Promise<PlanBasedBidPackagePage_BidPackageFragment | undefined>;
};

export const planBasedBidPackageFormConfig: ObjectConfig<PlanBasedBidPackageFormState> = {
  bidPackageGroupId: { type: "value" },
  bidPackageId: { type: "value" },
  marketId: { type: "value", rules: [required] },
  projects: { type: "value" },
  costCodes: { type: "value", rules: [required] },
  costType: { type: "value", rules: [required] },
  internalEstimateType: { type: "value" },
  productOfferings: { type: "value" },
  requests: {
    type: "list",
    config: { id: { type: "value" }, tradePartnerId: { type: "value" }, archive: { type: "value" } },
  },
  baseBidType: { type: "value" },
  pricingMode: { type: "value" },
  primaryCostCodeId: { type: "value" },
  bidPlanGroups: {
    type: "list",
    rules: [
      ({ value }) =>
        value?.some((g) => !g.archive.value)
          ? undefined
          : "Add at least one bid group (a base bid or an add-on option) to continue.",
    ],
    config: {
      id: { type: "value" },
      isNoCost: { type: "value" },
      globalOptionId: { type: "value" },
      primaryOptionGroupId: { type: "value" },
      order: { type: "value" },
      archive: { type: "value" },
      clientId: { type: "value" },
      dimensions: {
        type: "list",
        config: {
          id: { type: "value" },
          globalOptionTypeId: { type: "value" },
          globalOptionGroupId: { type: "value" },
          globalOptionIds: { type: "value" },
        },
      },
    },
  },
};

/**
 * Whether step 1 is complete — gates its Continue button and Configure Bid Groups. Keep in sync with
 * the fields step 1 marks `required`. Checks emptiness by hand: form-state's `required` accepts `[]`.
 *
 * Read synchronously from MobX-observed fields so callers (an `observer()` render, `useComputed`, or
 * a `reaction`) can track it as part of their own derivation.
 */
export function isCoreReady(formState: PlanBasedBidPackageFormObjectState): boolean {
  return (
    !!formState.marketId.value &&
    !!formState.projects.value?.nonEmpty &&
    !!formState.costCodes.value?.nonEmpty &&
    formState.costType.valid &&
    isPricingReady(formState)
  );
}

/** Whether the Pricing section is settled: "One Cost Code Covers the Others" needs its primary cost code picked. */
export function isPricingReady(formState: PlanBasedBidPackageFormObjectState): boolean {
  const { pricingMode, costCodes } = formState.value;
  const hasPricing = pricingMode === PricingMode.Primary && (costCodes?.length ?? 0) >= 2;
  return !hasPricing || !!primaryCostCodeIdToSave(formState.value);
}

/**
 * The primary cost code the form saves, if any: only "One Cost Code Covers the Others" over 2+ cost codes it is one
 * of counts, so a stale pick (e.g. its cost code was deselected) is simply not saved rather than synced away.
 */
export function primaryCostCodeIdToSave(form: PlanBasedBidPackageFormState): string | undefined {
  const { pricingMode, primaryCostCodeId, costCodes } = form;
  const selected = costCodes ?? [];
  const covers = pricingMode === PricingMode.Primary && selected.length >= 2;
  return covers && primaryCostCodeId && selected.includes(primaryCostCodeId) ? primaryCostCodeId : undefined;
}

/** Whether the form has at least one non-archived bid plan group (a base bid or an add-on option). */
export function hasBidGroups(formState: PlanBasedBidPackageFormObjectState): boolean {
  return (formState.bidPlanGroups.value ?? []).some((g) => !g.archive);
}

/**
 * Whether step 1 has edits Configure Bid Groups' query hasn't seen — it reads these back off the
 * *saved* bid package. Excludes `bidPlanGroups`, which are edited on that step itself, so gating on
 * them would grey out the tab the user is standing on.
 */
export function hasUnsavedPackageDetails(formState: PlanBasedBidPackageFormObjectState): boolean {
  return (
    formState.marketId.dirty ||
    formState.projects.dirty ||
    formState.costCodes.dirty ||
    formState.costType.dirty ||
    formState.productOfferings.dirty ||
    formState.primaryCostCodeId.dirty
  );
}

/** Builds a dimensions list from the checked type ids. Keeps existing rows as-is and seeds new ones with every value selected. */
export function buildDimensions(
  selectedTypeIds: string[],
  existing: PlanBasedBidPackageBidPlanGroupDimension[],
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"],
): PlanBasedBidPackageBidPlanGroupDimension[] {
  return selectedTypeIds.map((typeId) => {
    const prior = existing.find((d) => d.globalOptionTypeId === typeId);
    if (prior) return prior;
    // `type` is null on personalization results, so `?.` keeps us matching only GlobalOptionType ones.
    const result = dimensionResults.find((r) => r.type?.id === typeId);
    return { globalOptionTypeId: typeId, globalOptionIds: result?.options.map((go) => go.id) ?? [] };
  });
}

/** Same as `buildDimensions` but for personalization (group-based) dimensions, keyed by group id. */
export function buildPersonalizationDimensions(
  selectedGroupIds: string[],
  existing: PlanBasedBidPackageBidPlanGroupDimension[],
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"],
): PlanBasedBidPackageBidPlanGroupDimension[] {
  return selectedGroupIds.map((groupId) => {
    const prior = existing.find((d) => d.globalOptionGroupId === groupId);
    if (prior) return prior;
    // `group` is null on type-based results, so `?.` keeps us matching only personalization ones.
    const result = dimensionResults.find((r) => r.group?.id === groupId);
    return { globalOptionGroupId: groupId, globalOptionIds: result?.options.map((go) => go.id) ?? [] };
  });
}

/** True for base plan groups (no global option); options carry a globalOptionId. */
export function isBaseGroup(group: PlanBasedBidPackageBidPlanGroup): boolean {
  return !group.globalOptionId;
}

/** Returns the given bid package's product offerings sorted by id. */
export function getSortedPofs(bidPackage: PlanBasedBidsTableV2_BidPackageFragment): ProductOfferingObject[] {
  return bidPackage.productOfferings.sortBy((pof) => pof.id);
}

/**
 * Stable identity for a group across saves and reorders: the persisted id, or the FE-only `clientId` of an
 * unsaved group (not its globalOptionId, since an add-on can be added more than once).
 */
export function groupKey(group: PlanBasedBidPackageBidPlanGroup): string | undefined {
  return group.id ?? group.clientId ?? undefined;
}

/** Returns the next unused `<prefix>:N` client id, so unsaved group ids stay deterministic (no random/uuid). */
export function nextGroupClientId(groups: PlanBasedBidPackageBidPlanGroup[], prefix: "base" | "option"): string {
  const used = groups.map((g) => g.clientId).filter((id): id is string => !!id);
  let n = 1;
  while (used.includes(`${prefix}:${n}`)) n++;
  return `${prefix}:${n}`;
}

/**
 * Reassigns `order` across all groups so it stays globally unique and base-first:
 * base groups (by their current order) get 0..b-1, then options (by their current order) get b..b+n-1.
 * `order` is global — the bids table sorts every group by it — so it can't overlap between sections.
 */
export function renumberGroups(groups: PlanBasedBidPackageBidPlanGroup[]): PlanBasedBidPackageBidPlanGroup[] {
  const byOrder = (g: PlanBasedBidPackageBidPlanGroup) => g.order ?? Infinity;
  const ordered = [
    ...groups.filter(isBaseGroup).sortBy(byOrder),
    ...groups.filter((g) => !isBaseGroup(g)).sortBy(byOrder),
  ];
  const orderByGroup = new Map(ordered.map((g, i) => [g, i]));
  return groups.map((g) => ({ ...g, order: orderByGroup.get(g) }));
}

/**
 * Applies a section's new drag order then renumbers globally. `newKeys` are the dragged section's
 * group keys in their new order; groups not in `newKeys` (the other section) keep their relative order.
 * Works for both sections because `renumberGroups` sorts each section independently.
 */
export function reorderGroups(
  groups: PlanBasedBidPackageBidPlanGroup[],
  newKeys: string[],
): PlanBasedBidPackageBidPlanGroup[] {
  const indexByKey = new Map(newKeys.map((key, i) => [key, i]));
  const reordered = groups.map((g) => {
    const newIndex = indexByKey.get(groupKey(g) ?? "");
    return newIndex !== undefined ? { ...g, order: newIndex } : g;
  });
  return renumberGroups(reordered);
}

/**
 * Maps the form's bid plan groups to the exhaustive `saveBidPackage` input, dropping archived ones.
 * Sorted by `order` so the mutation's array matches step 2's display (zones then add-ons, after drag).
 */
export function mapToBidPlanGroupsInput(groups: PlanBasedBidPackageBidPlanGroup[]): SaveBidPlanGroupInput[] {
  return groups
    .filter((g) => !g.archive)
    .sortBy((g) => g.order ?? Infinity)
    .map((g) => ({
      id: g.id ?? undefined,
      order: g.order ?? undefined,
      isNoCost: g.isNoCost ?? undefined,
      globalOptionId: g.globalOptionId ?? undefined,
      primaryOptionGroupId: g.primaryOptionGroupId ?? undefined,
      dimensions: (g.dimensions ?? []).map((d) => ({
        id: d.id ?? undefined,
        // Personalization (group-based) dimensions carry the global option group;
        // type-based ones leave it undefined.
        globalOptionGroupId: d.globalOptionGroupId ?? undefined,
        globalOptionTypeId: d.globalOptionTypeId ?? undefined,
        globalOptionIds: d.globalOptionIds ?? [],
      })),
    }));
}

// Whether a global option fills the given dimension's column. Mirrors the backend's
// `BidPlanGroupDimension.matchesGlobalOption`: personalization dimensions match by the option's
// group, type dimensions match by the option's type.
export function optionMatchesDimension(
  globalOption: { type: { id: string }; group: { id: string } },
  dim: PlanBasedBidsTableV2_BidPlanGroupDimensionFragment,
): boolean {
  return dim.globalOptionGroup
    ? globalOption.group.id === dim.globalOptionGroup.id
    : globalOption.type.id === dim.globalOptionType?.id;
}

// A dimension is keyed by either its global option type or (for personalization) its global option
// group — never both. We use this key to line up a row's options with the dimension columns.
export function bidPlanGroupDimensionKey(dim: PlanBasedBidsTableV2_BidPlanGroupDimensionFragment): string {
  return dim.globalOptionType?.id ?? dim.globalOptionGroup?.id ?? fail("Dimension has neither a type nor a group");
}

// Order dimensions by their global option type's `order`. Personalization (group-based) dimensions
// reach their type through the global option group, so they sort alongside the type-based ones.
export function orderedBidPlanGroupDimensions(
  dimensions: PlanBasedBidsTableV2_BidPlanGroupDimensionFragment[],
): PlanBasedBidsTableV2_BidPlanGroupDimensionFragment[] {
  return dimensions.sortBy(
    (dim) => (dim.globalOptionType ?? dim.globalOptionGroup?.type)?.order ?? Number.MAX_SAFE_INTEGER,
  );
}

export type SectionSearch = { hidden: boolean; tableFilter: string | undefined };

/**
 * Applies the shared search box to one header+GridTable section (see PlanBasedBidsTables /
 * PlanBasedScopeTable), where the section title lives outside the table so GridTable's own
 * filter can't see it. Terms matched by the title are absorbed by it (i.e. searching a group's title
 * shows its whole table). Only the leftover terms are passed on as the table's filter, and
 * when no row would match them the section hides entirely.
 */
export function applySectionSearch<R extends Kinded>(
  searchFilter: string | undefined,
  sectionTitle: string,
  rows: GridDataRow<R>[],
  columns: GridColumn<R>[],
): SectionSearch {
  const terms = searchFilter ? searchFilter.trim().split(/ +/) : [];
  if (terms.isEmpty) return { hidden: false, tableFilter: undefined };
  const rowTerms = terms.filter((t) => !matchesFilter(sectionTitle, t));
  if (rowTerms.isEmpty) return { hidden: false, tableFilter: undefined };
  const columnsWithIds = assignDefaultColumnIds(columns);
  // beam passes each row's GridRowApi here, but none of our tables' cells read it, so skip building one
  const api = undefined as any;
  function rowMatches(row: GridDataRow<R>): boolean {
    const self =
      !reservedRowKinds.includes(row.kind) &&
      rowTerms.every((term) => columnsWithIds.some((c) => matchesFilter(applyRowFn(c, row, api, 0, false), term)));
    return self || (row.children ?? []).some(rowMatches);
  }
  return { hidden: !rows.some(rowMatches), tableFilter: rowTerms.join(" ") };
}

/** Narrows a `costType` query param to a `CostType`, e.g. to prefill the create steppers. */
export function parseCostType(value: string | null | undefined): CostType | undefined {
  return Object.values(CostType).includes(value as CostType) ? (value as CostType) : undefined;
}
